import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, Undo2 } from 'lucide-react';
import { ordersApi, authApi } from '@verlo/shared/services/api';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { useDrawer } from '../../contexts/DrawerContext';
import { refundableLines, amountFor, allPicks } from '../../lib/refundLines';

// Keep in sync with backend/routes/orders.py REFUND_REASONS.
const REASONS = [
  { key: 'wrong_item', label: 'Wrong item / mistake' },
  { key: 'quality',    label: 'Quality complaint' },
  { key: 'delayed',    label: 'Excessive wait / delay' },
  { key: 'duplicate',  label: 'Duplicate charge' },
  { key: 'goodwill',   label: 'Goodwill gesture' },
  { key: 'other',      label: 'Other (see notes)' },
];

// Roles that can authorise a refund. Mirrors pin_auth.MANAGER_OVERRIDE_ROLES
// intersected with who actually holds `epos.refund`. This only shapes the
// picker — the server is the one that enforces it.
const AUTHORISING_ROLES = new Set([
  'manager', 'general_manager', 'area_manager', 'operations', 'owner',
]);
const LEGACY_AUTHORISING_ROLES = new Set(['manager', 'admin', 'owner', 'super_admin']);

const canAuthorise = (u) =>
  AUTHORISING_ROLES.has((u.rbac_role || '').toLowerCase()) ||
  LEGACY_AUTHORISING_ROLES.has((u.role || '').toLowerCase());

/**
 * Manager-approved refund modal for a single paid order.
 *
 * A server can raise the refund on their own terminal, but a manager has to pick
 * themselves from the list and enter their PIN. Naming the manager is what lets
 * the backend verify exactly one PIN hash rather than testing the entered PIN
 * against every staff member — see pin_auth.authorise_manager_pin.
 *
 * All refunds land in the audit log + `refunds` collection so they surface
 * on the Discounts & Refunds report.
 */
// A field's problem, under the field. Empty when there's nothing to say.
const FieldError = ({ words, testId, id }) => (words ? (
  <p className="text-[12.5px] font-medium text-signal-stop" role="alert" id={id} data-testid={testId}>{words}</p>
) : null);

const RefundDialog = ({
  open,
  onOpenChange,
  order,
  onRefunded,
  formatCurrency = (v) => `£${(v || 0).toFixed(2)}`,
}) => {
  const priorRefunded = order?.refund_amount || 0;
  const total = order?.total || 0;
  const maxRefundable = Math.max(0, total - priorRefunded);

  const [amount, setAmount] = useState('');
  // The dishes being refunded, { line_id: quantity }. Ticking them is the refund; the amount
  // field is for a refund that isn't about a dish (a goodwill gesture).
  const [picks, setPicks] = useState({});
  const dishes = refundableLines(order);
  const byDish = Object.keys(picks).length > 0;
  const dishAmount = byDish ? amountFor(order, picks) : 0;
  const togglePick = (l) => setPicks((prev) => {
    const next = { ...prev };
    if (next[l.line_id]) delete next[l.line_id]; else next[l.line_id] = l.left;
    return next;
  });
  const stepPick = (l, d) => setPicks((prev) => {
    const q = Math.max(1, Math.min(l.left, (prev[l.line_id] || 0) + d));
    return { ...prev, [l.line_id]: q };
  });
  const everything = allPicks(order);
  const allPicked = Object.keys(everything).length > 0
    && Object.entries(everything).every(([id, q]) => picks[id] === q);
  // How the money goes back. Cash comes out of the drawer and lands on the cash-off; card
  // goes back through the PDQ. A terminal with no drawer refunds by card only.
  const { hasDrawer, refresh: refreshDrawer } = useDrawer();
  const [method, setMethod] = useState('card');
  const [reason, setReason] = useState('wrong_item');
  const [notes, setNotes] = useState('');
  const [pin, setPin] = useState('');
  const [managerId, setManagerId] = useState('');
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // What's wrong, said under the field it's about rather than in a toast across the room:
  // { amount, manager, pin }. Typing in a field clears its own.
  const [errors, setErrors] = useState({});
  const fail = (field, words) => setErrors((e) => ({ ...e, [field]: words }));

  useEffect(() => {
    if (open) {
      // Nothing pre-filled: the whole bill used to be, and a manager typed it down to the dish.
      setErrors({});
      setAmount('');
      setPicks({});
      setMethod(hasDrawer && order?.payment_method === 'cash' ? 'cash' : 'card');
      setReason('wrong_item');
      setNotes('');
      setPin('');
      setManagerId('');
    }
  }, [open, maxRefundable]); // eslint-disable-line react-hooks/exhaustive-deps

  const siteId = order?.site_id;
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingManagers(true);
    authApi.pinStaffGrid(siteId)
      .then((res) => {
        if (cancelled) return;
        setManagers((res.data?.users || []).filter(canAuthorise));
      })
      .catch(() => {
        if (!cancelled) setManagers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingManagers(false);
      });
    return () => { cancelled = true; };
  }, [open, siteId]);

  const submit = async () => {
    const amt = byDish ? dishAmount : parseFloat(amount);
    const found = {};
    if (!amt || amt <= 0) found.amount = byDish ? 'Tick a dish, or enter an amount' : 'Enter an amount to refund';
    // Compare in pence — the backend does, and 0.1 + 0.2 > 0.3 in float.
    else if (Math.round(amt * 100) > Math.round(maxRefundable * 100)) found.amount = `No more than ${formatCurrency(maxRefundable)} can be refunded`;
    if (!managerId) found.manager = 'Choose the manager authorising this refund';
    if (!pin) found.pin = 'Enter the manager’s PIN';
    else if (!/^\d{4,6}$/.test(pin)) found.pin = 'A PIN is 4 to 6 digits';
    setErrors(found);
    if (Object.keys(found).length) return;
    setSubmitting(true);
    try {
      const res = await ordersApi.refund(order.id, {
        ...(byDish
          ? { lines: Object.entries(picks).map(([line_id, quantity]) => ({ line_id, quantity })) }
          : { amount: amt }),
        method,
        reason,
        manager_id: managerId,
        manager_pin: pin,
        notes,
      });
      toast.success(`Refund of ${formatCurrency(amt)} ${method === 'cash' ? 'given in cash' : 'to the card'}`);
      if (method === 'cash') refreshDrawer();
      onRefunded?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        fail('pin', typeof detail === 'string' ? detail : 'That PIN isn’t right for this manager');
        setPin('');
      } else if (status === 423 || status === 429) {
        // Too many wrong PINs — locked out by pin_auth's counters.
        fail('pin', typeof detail === 'string' ? detail : 'Too many wrong PINs. Try again in a moment');
        setPin('');
      } else if (status === 409) {
        toast.error(detail || 'This order changed while you were authorising. Reopen it and try again.');
        onOpenChange(false);
      } else if (status === 400) {
        toast.error(detail || 'Refund could not be processed');
      } else {
        toast.error('Refund failed. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const noManagers = !loadingManagers && managers.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="form" data-testid="refund-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-signal-stop">
            <ShieldAlert size={18} />
            <DialogTitle className="text-lg">Manager refund</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-surface-2 rounded-lg p-3 text-[13px]">
            <div className="flex justify-between"><span className="text-ink-soft">Order total</span><span className="font-semibold">{formatCurrency(total)}</span></div>
            {priorRefunded > 0 && (
              <div className="flex justify-between text-signal-warn"><span>Already refunded</span><span className="font-semibold">{formatCurrency(priorRefunded)}</span></div>
            )}
            <div className="flex justify-between pt-1.5 mt-1.5 border-t border-hairline text-ink"><span>Max refundable</span><span className="font-bold">{formatCurrency(maxRefundable)}</span></div>
          </div>

          {dishes.length > 0 && (
            <div className="border border-hairline bg-white" data-testid="refund-dishes">
              <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">Dishes</span>
                <button
                  type="button"
                  className="text-[11px] font-bold uppercase tracking-[0.06em] text-verlo-hover disabled:text-ink-faint"
                  onClick={() => setPicks(allPicked ? {} : everything)}
                  disabled={submitting || Object.keys(everything).length === 0}
                  data-testid="refund-all"
                >
                  {allPicked ? 'None' : 'All'}
                </button>
              </div>
              {dishes.map((l) => {
                const on = !!picks[l.line_id];
                const gone = l.left <= 0;
                return (
                  <div key={l.line_id} className={`flex items-center gap-2.5 border-b border-hairline px-3 py-2 last:border-b-0 ${on ? 'bg-verlo-tint' : ''}`}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      disabled={gone || submitting}
                      onClick={() => togglePick(l)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:opacity-50"
                      data-testid={`refund-dish-${l.line_id}`}
                    >
                      <span className={`grid h-[18px] w-[18px] shrink-0 place-items-center border text-[12px] text-white ${on ? 'border-verlo bg-verlo' : 'border-hairline-strong bg-white'}`}>{on ? '✓' : ''}</span>
                      <span className="truncate text-[13px]">{l.ordered}× {l.name}{gone ? ' · refunded' : ''}</span>
                    </button>
                    {on && l.left > 1 && (
                      <span className="flex items-center gap-1.5 text-[12px] text-ink-soft">
                        <Button type="button" variant="outline" size="sm" className="h-6 px-2" onClick={() => stepPick(l, -1)}>−</Button>
                        {picks[l.line_id]} of {l.left}
                        <Button type="button" variant="outline" size="sm" className="h-6 px-2" onClick={() => stepPick(l, 1)}>+</Button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {byDish ? (
            <div className="flex items-baseline justify-between px-0.5" data-testid="refund-dish-total">
              <span className="text-xs text-ink-soft">To refund</span>
              <span className="font-mono text-xl font-bold tabular-nums">{formatCurrency(dishAmount)}</span>
            </div>
          ) : (
          <div className="space-y-1.5">
            <Label className="text-xs">{dishes.length > 0 ? 'Or an amount, for a refund that isn’t a dish' : 'Refund amount'}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={maxRefundable}
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setErrors((x) => ({ ...x, amount: null })); }}
                aria-invalid={!!errors.amount}
                className={`pl-7 h-10 text-lg font-mono-display ${errors.amount ? 'border-signal-stop-line' : ''}`}
                data-testid="refund-amount"
                disabled={submitting}
              />
            </div>
            <FieldError words={errors.amount} testId="refund-amount-error" />
          </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Refund how</Label>
            <Segmented
              stretch
              value={method}
              onChange={setMethod}
              options={[
                { value: 'card', label: 'Back to card', testId: 'refund-method-card' },
                ...(hasDrawer ? [{ value: 'cash', label: 'Cash from drawer', testId: 'refund-method-cash' }] : []),
              ]}
              data-testid="refund-method"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason} disabled={submitting}>
              <SelectTrigger className="h-10" data-testid="refund-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reason === 'other' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explain the refund reason…"
                className="min-h-[60px] text-sm"
                data-testid="refund-notes"
                disabled={submitting}
              />
            </div>
          )}

          <div className="space-y-1.5 pt-1 border-t border-hairline">
            <Label className="text-xs">Authorising manager</Label>
            <Select
              value={managerId}
              onValueChange={(v) => { setManagerId(v); setErrors((x) => ({ ...x, manager: null })); }}
              disabled={submitting || loadingManagers || noManagers}
            >
              <SelectTrigger className={`h-10 ${errors.manager ? 'border-signal-stop-line' : ''}`} data-testid="refund-manager" aria-invalid={!!errors.manager}>
                <SelectValue
                  placeholder={
                    loadingManagers ? 'Loading…'
                      : noManagers ? 'No manager available on this site'
                      : 'Select manager'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError words={errors.manager} testId="refund-manager-error" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <ShieldAlert size={11} className="text-signal-stop" />
              Manager PIN
            </Label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setErrors((x) => ({ ...x, pin: null })); }}
              placeholder="••••"
              aria-invalid={!!errors.pin}
              aria-describedby={errors.pin ? 'refund-pin-error' : undefined}
              className={`text-center text-2xl font-mono-display tracking-[0.5em] h-11 ${errors.pin ? 'border-signal-stop-line ring-1 ring-signal-stop-line' : ''}`}
              data-testid="refund-manager-pin"
              disabled={submitting || noManagers}
              autoComplete="off"
            />
            <FieldError words={errors.pin} testId="refund-pin-error" id="refund-pin-error" />
            <p className="text-[10px] text-ink-faint leading-relaxed">
              A manager, admin, or owner PIN is required. Every refund is written to the audit log
              and appears on the Discounts &amp; Refunds report.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="refund-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || maxRefundable <= 0 || noManagers}
            className="bg-signal-stop hover:bg-signal-stop gap-1.5"
            data-testid="refund-confirm"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
            Refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundDialog;
